import { Alert, Autocomplete, Box, Stack, SvgIcon, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getInitialTemplate } from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import { SendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import { sendDrawerNeedsExtractedRecipients } from 'components/@home/@documents/SentDocuments/sendDrawerStepKeys';
import DropzoneBox from 'components/common/DropzoneBox';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import Attachments from 'services/api/Attachments';
import { func, number as numberProp, string as stringProp } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useListDocumentFormatsQuery } from 'store/app/entities/SharedFilesSlice';
import 'store/selectors/authCompany';
import authCompanyId from 'store/selectors/authCompanyId';

import filesize from 'utils/filesize';
import { I18n } from 'utils/i18n';

const getOptionLabel = option => option?.name || '';

const idEq = (a, b) => String(a ?? '') === String(b ?? '');

const UploadedFileCard = ({ name, size = 0 }) => (
  <Stack
    spacing={0.5}
    sx={[
      {
        alignItems: 'center',
      },
      theme => ({
        width: '100%',
        py: 2,
        px: 3,
        borderRadius: 1,
        color: theme.palette.success.dark || theme.palette.success.main,
      }),
    ]}
  >
    <SvgIcon fontSize="large" sx={{ color: 'success.main' }}>
      <CheckCircleIcon />
    </SvgIcon>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
      {name}
      {size ? ` · ${filesize(size)}` : ''}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
      }}
    >
      {I18n.t('HrBot.DocumentStep.clickToReplace')}
    </Typography>
  </Stack>
);

UploadedFileCard.propTypes = {
  name: stringProp.isRequired,
  size: numberProp,
};

const StepDocumentAndMode = ({ setNextEnabled, setError }) => {
  const { file: format, setFile, allowFormatSelection } = useContext(SendDrawerContext);
  const companyId = useSelector(authCompanyId);

  const [uploadBusy, setUploadBusy] = useState(false);
  const [sessionFileMeta, setSessionFileMeta] = useState(null);
  const {
    data: { documentFormats = [] } = {},
    isLoading,
    isUninitialized,
  } = useListDocumentFormatsQuery();

  const templateOptions = documentFormats;
  const showTemplateAutocomplete = allowFormatSelection;

  const selectedFromList = useMemo(
    () => (format?._id ? templateOptions.find(df => idEq(df._id, format._id)) || format : null),
    [templateOptions, format],
  );

  const comboValue = selectedFromList || (format?._id ? format : null);

  const perSendDropHintKey =
    (format?.signingMode || 'onePerMember') === 'oneForAll'
      ? 'HrBot.DocumentStep.perSendSessionDropHintMulti'
      : 'HrBot.DocumentStep.perSendSessionDropHintSingle';

  const needsPerSendSessionPdf = useMemo(
    () =>
      !!format?._id &&
      format.documentSource === 'per-send' &&
      !sendDrawerNeedsExtractedRecipients(format) &&
      !format.sendSessionAttachmentId,
    [format],
  );

  useEffect(() => {
    const hasListTemplate = !!format?._id;
    const hasScratchPdf =
      !format?._id &&
      (!!format?.templateAttachment ||
        !!format?.templateAttachment?._id ||
        !!format?.layoutSampleAttachment);
    const perSendOk =
      !hasListTemplate ||
      format.documentSource !== 'per-send' ||
      sendDrawerNeedsExtractedRecipients(format) ||
      !!format.sendSessionAttachmentId;
    const ok = !!format && ((hasListTemplate && perSendOk) || hasScratchPdf);
    setError(null);
    setNextEnabled(ok);
  }, [format, setError, setNextEnabled]);

  const handlePickTemplate = useCallback(
    (_e, value) => {
      setSessionFileMeta(null);
      if (!value) {
        setFile(null);
        return;
      }
      const next = { ...value };
      if (next.documentSource === 'per-send') {
        next.sendSessionAttachmentId = null;
      } else {
        delete next.sendSessionAttachmentId;
      }
      setFile(next);
    },
    [setFile],
  );

  const handleDropPdf = useCallback(
    async acceptedFiles => {
      const list = (acceptedFiles || []).filter(Boolean);
      if (!list.length || !companyId) return;
      setUploadBusy(true);
      setError(null);
      try {
        const file = list[0];
        const uploaded = await Attachments.upload(companyId, 'none', file, null, null, null, null);
        const attachmentId = uploaded?._id ?? uploaded?.id;
        if (!attachmentId) {
          setError(I18n.t('Something went wrong with sending data'));
          return;
        }
        if (
          format?._id &&
          format.documentSource === 'per-send' &&
          !sendDrawerNeedsExtractedRecipients(format)
        ) {
          setSessionFileMeta({ name: file.name, size: file.size });
          // Do not compare f._id to format._id — ad-hoc list picks often mismatch ObjectId vs string.
          setFile(f => {
            if (
              !f?._id ||
              f.documentSource !== 'per-send' ||
              sendDrawerNeedsExtractedRecipients(f)
            ) {
              return f;
            }
            return { ...f, sendSessionAttachmentId: attachmentId };
          });
          return;
        }
        if (format?._id) {
          return;
        }
        setSessionFileMeta({ name: file.name, size: file.size });
        const base = getInitialTemplate(null, I18n.t('HrBot.defaultMessage'));
        setFile({
          ...base,
          name: file.name?.replace(/\.pdf$/i, '') || base.name,
          templateAttachment: attachmentId,
          documentSource: 'fixed',
          type: 'pdf',
        });
      } catch (e) {
        setError(e?.message || String(e));
      } finally {
        setUploadBusy(false);
      }
    },
    [companyId, format, setError, setFile],
  );

  const showPerSendDropzone =
    !!format?._id &&
    format.documentSource === 'per-send' &&
    !sendDrawerNeedsExtractedRecipients(format);

  /** Ad-hoc PDF without a list template (scratch path uses templateAttachment, not sendSessionAttachmentId). */
  const hasScratchPdfUploaded = useMemo(
    () =>
      !format?._id &&
      (!!format?.templateAttachment ||
        !!format?.templateAttachment?._id ||
        !!format?.layoutSampleAttachment),
    [format],
  );

  const frameSuccessTint =
    (showPerSendDropzone && !!format?.sendSessionAttachmentId) || hasScratchPdfUploaded;

  /** Fixed template or per-send with extraction: template row only (no session PDF drop on this step). */
  const showListTemplateWithoutSessionDrop = !!format?._id && !showPerSendDropzone;

  /** Per-send session PDF or scratch-from-empty: Acknowledge-style dashed area contains template + upload. */
  const showDocumentDropzone = showPerSendDropzone || !format?._id;

  const dropzonePrepend = useMemo(
    () => (
      <>
        {showTemplateAutocomplete && (
          <Autocomplete
            loading={isLoading || isUninitialized}
            options={templateOptions}
            value={comboValue}
            onChange={handlePickTemplate}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(a, b) => idEq(a?._id, b?._id)}
            sx={{ width: '100%' }}
            renderInput={params => (
              <TextField
                {...params}
                label={I18n.t('HrBot.DocumentStep.chooseTemplate')}
                variant="outlined"
                margin="dense"
              />
            )}
          />
        )}
        {!allowFormatSelection && format?.name && (
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {format.name}
          </Typography>
        )}
        {needsPerSendSessionPdf && (
          <Alert
            severity="info"
            sx={{ mt: allowFormatSelection || (!allowFormatSelection && format?.name) ? 1.5 : 0 }}
          >
            {I18n.t('HrBot.DocumentStep.perSendSessionNotice')}
          </Alert>
        )}
      </>
    ),
    [
      allowFormatSelection,
      comboValue,
      format?.name,
      handlePickTemplate,
      isLoading,
      isUninitialized,
      needsPerSendSessionPdf,
      showTemplateAutocomplete,
      templateOptions,
    ],
  );

  const sessionUploadPlaceholder =
    showPerSendDropzone && format?.sendSessionAttachmentId ? (
      <UploadedFileCard
        name={sessionFileMeta?.name || I18n.t('HrBot.DocumentStep.perSendSessionReady')}
        size={sessionFileMeta?.size}
      />
    ) : null;

  const scratchUploadPlaceholder =
    !format?._id && hasScratchPdfUploaded ? (
      <UploadedFileCard
        name={
          sessionFileMeta?.name || format?.name || I18n.t('HrBot.DocumentStep.perSendSessionReady')
        }
        size={sessionFileMeta?.size}
      />
    ) : null;

  const uploadPlaceholder = sessionUploadPlaceholder || scratchUploadPlaceholder;

  /** Send from template row: template is fixed — avoid “pick a template” title/help. */
  const isPreselectedPerSendSessionStep =
    !allowFormatSelection &&
    !!format?._id &&
    format.documentSource === 'per-send' &&
    !sendDrawerNeedsExtractedRecipients(format);

  return (
    <Box sx={{ p: 2, maxWidth: 720 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {isPreselectedPerSendSessionStep
          ? I18n.t('HrBot.Steps.PerSendSessionDocument')
          : I18n.t('HrBot.Steps.DocumentMode')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {isPreselectedPerSendSessionStep
          ? I18n.t('HrBot.DocumentStep.perSendHelp')
          : I18n.t('HrBot.DocumentStep.help')}
      </Typography>

      {showListTemplateWithoutSessionDrop && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'primary.ultraLight',
            boxSizing: 'border-box',
          }}
        >
          {allowFormatSelection ? (
            <Autocomplete
              loading={isLoading || isUninitialized}
              options={documentFormats}
              value={comboValue}
              onChange={handlePickTemplate}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={(a, b) => idEq(a?._id, b?._id)}
              sx={{ width: '100%' }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={I18n.t('HrBot.DocumentStep.chooseTemplate')}
                  variant="outlined"
                  margin="dense"
                />
              )}
            />
          ) : (
            format?.name && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {format.name}
              </Typography>
            )
          )}
        </Box>
      )}

      {showDocumentDropzone && (
        <DropzoneBox
          prependContent={dropzonePrepend}
          loading={uploadBusy}
          onDrop={handleDropPdf}
          accept="application/pdf"
          framed
          frameSx={
            frameSuccessTint
              ? theme => ({
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                })
              : {}
          }
          sx={{
            mt: 2,
            ...(showPerSendDropzone ? { minHeight: 140 } : {}),
          }}
          hint={showPerSendDropzone ? I18n.t(perSendDropHintKey) : undefined}
          placeholder={uploadPlaceholder}
        />
      )}
    </Box>
  );
};

StepDocumentAndMode.propTypes = {
  setNextEnabled: func.isRequired,
  setError: func.isRequired,
};

export default StepDocumentAndMode;
